import type { Offer } from '@chicare/shared';
import { cn } from '@/lib/cn';
import { Badge } from './Badge';
import { Button } from './Button';

/**
 * OfferRow — one rung of the offer ladder.
 *
 * §10 Phase 2: "product page with the offer ladder as hero".
 *
 * Eighteen pharmacies sell the same paracetamol. Seeing them ranked by price,
 * distance and expiry is what a marketplace is FOR, and this row is where that
 * comparison actually happens. Each rung therefore carries the four things a
 * buyer weighs against each other and nothing else:
 *
 *   who is selling it (and whether their PCN licence is live)
 *   how far away and how soon
 *   what the batch expiry is
 *   the price
 *
 * The BEST rung is decided by the server, not here, so the web app and the
 * React Native app can never disagree about which offer is cheapest.
 */

export interface OfferRowProps {
  offer: Offer;
  onAdd?: (offer: Offer) => void;
  adding?: boolean;
  className?: string;
}

export function OfferRow({ offer, onAdd, adding = false, className }: OfferRowProps) {
  const best = offer.is_best_price;
  const batch = offer.batch;

  return (
    <div
      className={cn(
        'relative flex items-center gap-[11px] rounded-[10px] border bg-white px-3 py-[11px]',
        best
          ? 'border-blue bg-blue-tint-2 shadow-[inset_0_0_0_1px_var(--color-blue)]'
          : 'border-line',
        className,
      )}
    >
      {best ? (
        <span className="absolute -top-[7px] left-2.5 rounded px-[7px] py-0.5 font-mono text-[9px] tracking-[0.08em] bg-blue text-white">
          BEST
        </span>
      ) : null}

      <span className="grid h-[34px] w-[34px] shrink-0 place-items-center rounded-[9px] bg-blue-tint font-display text-[13px] font-bold text-blue">
        {offer.pharmacy.initials}
      </span>

      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-[5px]">
          <b className="truncate text-[12.5px]">{offer.pharmacy.name}</b>
          {/* No verified mark here either — see HomeScreen. Every offer in
              this list comes from a licensed pharmacy, so marking each one
              said nothing and crowded the name. */}
        </div>

        <div className="mt-[3px] flex flex-wrap items-center gap-1.5">
          {offer.pharmacy.distance_km !== undefined ? (
            <span className="text-[11px] text-faint">{offer.pharmacy.distance_km} km</span>
          ) : offer.pharmacy.lga ? (
            <span className="text-[11px] text-faint">{offer.pharmacy.lga}</span>
          ) : null}
          {offer.delivery_options.includes('standard') ? (
            <>
              <span className="text-[11px] text-faint">·</span>
              <span className="text-[11px] text-faint">Today by 5pm</span>
            </>
          ) : null}
        </div>

        <div className="mt-[3px] flex flex-wrap items-center gap-1.5">
          {batch ? (
            <Badge tone={batch.expiry_state === 'ok' ? 'verified' : 'caution'}>
              {batch.expiry_state === 'ok'
                ? `Exp ${batch.expiry_label}`
                : `Exp in ${batch.months_remaining}mo`}
            </Badge>
          ) : (
            <Badge tone="danger">Out of stock</Badge>
          )}
          <span className="text-[11px] tracking-[0.5px] text-caution">
            ★ {offer.pharmacy.quality_score.toFixed(1)}
          </span>
        </div>
      </div>

      <div className="shrink-0 text-right">
        <div className="font-display text-[17px] font-bold tracking-[-0.02em] tabular">
          {offer.price_display}
        </div>
        {onAdd ? (
          <Button
            size="xs"
            variant={best ? 'primary' : 'ghost'}
            loading={adding}
            disabled={!offer.in_stock}
            onClick={() => onAdd(offer)}
            className="mt-1.5"
            aria-label={`Add ${offer.pharmacy.name} offer at ${offer.price_display} to cart`}
          >
            Add
          </Button>
        ) : null}
      </div>
    </div>
  );
}
